import { LastDetectionsMapping } from "@/shared/types";
import { useViewSettings } from "@/view-settings";
import { hiddenTracks$, visibleTracksSnapshots$ } from "@/virtual-store/state";
import { useSelector } from "@legendapp/state/react";
import { Button } from "./button";
import IconX from "~icons/tabler/x";
import { Rnd } from "react-rnd";
import { useSetState } from "react-use";
import { ScrollArea } from "@radix-ui/themes";
import { AnimalAvatar } from "./animal-avatar";

export function TracksLastDetectionsMapping() {
  const viewSettings = useViewSettings();

  const isEnabled = useSelector(() =>
    viewSettings.showLastDetectionsPanel.get()
  );

  if (!isEnabled) {
    return null;
  }

  return <DetectionsTable />;
}

function DetectionsTable() {
  const viewSettings = useViewSettings();
  const [size, setSize] = useSetState({
    x: 900,
    y: 270,
    width: 300,
    height: 150,
  });

  const toggle = () => viewSettings.showLastDetectionsPanel.toggle();

  const data = useSelector(() => {
    const hiddenTracks = hiddenTracks$.get();
    const visibleTracksSnapshots = visibleTracksSnapshots$.get();

    if (!visibleTracksSnapshots) return [];

    const entries = [...visibleTracksSnapshots.entries()];

    const info = entries
      .filter(([trackId]) => !hiddenTracks.has(trackId))
      .map(([trackId, snapshot]) => {
        const lastDetectionsMapping: LastDetectionsMapping[] =
          typeof snapshot.last_detections_mapping === "string"
            ? JSON.parse((snapshot.last_detections_mapping as string) || "{}")
            : snapshot.last_detections_mapping;

        const cameraIds = lastDetectionsMapping.map(
          (detection) => detection.sensor_id
        );
        const uniqueCameraIds = new Set(cameraIds);

        return {
          trackId,
          sensorIds: Array.from(uniqueCameraIds),
        };
      });

    return info;
  });

  return (
    <Rnd
      bounds="parent"
      className="z-[100]"
      cancel=".no-draggable"
      size={{ width: size.width, height: size.height }}
      position={{ x: size.x, y: size.y }}
      minWidth={300}
      minHeight={150}
      onDragStop={(_, d) => {
        setSize({ x: d.x, y: d.y });
      }}
      onResize={(_, ___, ref, __, position) => {
        setSize({
          width: ref.offsetWidth,
          height: ref.offsetHeight,
          ...position,
        });
      }}
    >
      <div className="bg-[var(--gray-1)] h-full rounded-lg overflow-hidden">
        <header className="bg-[var(--gray-1)] flex justify-between items-center px-4 p-1 pr-1">
          <span className="font-semibold mr-4">Last detections mapping</span>
          <Button className="no-draggable p-2" onClick={toggle}>
            <IconX />
          </Button>
        </header>

        <ScrollArea>
          <div className="relative px-4 py-2 pb-5">
            <table className="w-full text-sm text-left rtl:text-right">
              <colgroup>
                <col className="sticky left-0 min-w-[75px] w-[75px] max-w-[75px]"></col>
                <col className="min-w-[200px]"></col>
              </colgroup>
              <thead className="text-xs bg-[var(--gray-a3)] uppercase">
                <tr>
                  <th className="px-2 py-1">Avatar</th>
                  <th className="px-2 py-1">Sensor IDs</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr className="even:bg-[var(--gray-a3)] odd:bg-[var(--gray-a1)] border-b border-[var(--gray-a4)] no-draggable">
                    <td className="px-2 py-1 font-medium max-w-[40px] whitespace-nowrap">
                      <AnimalAvatar
                        size="30px"
                        trackId={item.trackId}
                        enableHoverZoom
                        zoomScale={3}
                      />
                    </td>
                    <td className="px-2 py-1 font-medium whitespace-nowrap">
                      {item.sensorIds
                        .sort((a, b) => Number(a) - Number(b))
                        .toString()
                        .split(",")
                        .join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ScrollArea>
      </div>
    </Rnd>
  );
}
